// The frame every screen is drawn in: a title, the three states a fetch can be
// in, and the search box and filter menu where a screen has them.
//
// An error is rendered rather than logged, and the API's message is what it
// says: it is written to be shown, and a client that swallowed it would present
// a working instance as a broken application.

import type { ReactNode } from 'react';
import { ApiError } from '../api';
import { FilterMenu, type FilterMenuProps } from './FilterMenu';

export interface SearchBox {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onClear: () => void;
}

export interface ScreenProps {
  title: string;
  description?: string;
  loading?: boolean;
  error?: Error | null;
  count?: number;
  search?: SearchBox;
  filters?: FilterMenuProps;
  actions?: ReactNode;
  children: ReactNode;
}

export function Screen({ title, description, loading, error, count, search, filters, actions, children }: ScreenProps) {
  return (
    <section className="screen">
      <header className="screen-head">
        <div>
          <h1>{title}</h1>
          {description && <p className="screen-description">{description}</p>}
        </div>
        <div className="screen-tools">
          {search && (
            <form
              className="search"
              onSubmit={(event) => {
                event.preventDefault();
                search.onSubmit();
              }}
            >
              <input
                type="search"
                value={search.value}
                placeholder="Search"
                aria-label="Search"
                onChange={(event) => search.onChange(event.target.value)}
              />
              <button type="submit">Search</button>
              {search.value && (
                <button type="button" className="link" onClick={search.onClear}>
                  Clear
                </button>
              )}
            </form>
          )}
          {filters && <FilterMenu {...filters} />}
          {actions}
        </div>
      </header>

      {error && <ErrorNote error={error} />}
      {loading ? <p className="muted">Loading…</p> : children}
      {!loading && count !== undefined && (
        <p className="muted count">
          {count.toLocaleString()} {count === 1 ? 'row' : 'rows'}
        </p>
      )}
    </section>
  );
}

export function ErrorNote({ error }: { error: Error }) {
  const api = error instanceof ApiError ? error : null;
  return (
    <div className="error" role="alert">
      <strong>{api ? `${api.status} ${api.code}` : error.name}</strong>
      <p>{error.message}</p>
      {api?.details && (
        <ul>
          {Object.entries(api.details).map(([field, message]) => (
            <li key={field}>
              <code>{field}</code>: {message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
